import React, { useState, useEffect, useRef } from 'react';
import { Card } from '@/components/ui/card';
import {
  AgentMetricsTableHeader,
  AgentMetricsTableContent,
} from './components';
import { TablePagingFooter } from '@/components/widgets/common/TablePagingFooter';
import { useAgentMetricsTable } from './hooks';
import { TotalHandledCallsModal } from './components/TotalHandledCallsModal';
import { TransferredRateModal } from './components/TransferredRateModal';
import { SLAModal } from './components/SLAModal';

type ModalType = 'handledCalls' | 'transferredRate' | 'sla' | null;

interface AgentPerformanceDetailTableProps {
  limit?: number;
  searchTerm?: string;
  onSearchChange?: (searchTerm: string) => void;
  dateParams?: {
    startDate: string;
    endDate: string;
    previousStartDate: string;
    previousEndDate: string;
    queueMicrosoftIds?: string[] | null;
    agentMicrosoftIds?: string[] | null;
    groupIds?: number[] | null;
    widgetIds?: number[];
    widgetPagination?: Record<string, { page: number; limit: number }>;
    timeStamp?: string;
  };
}

const AgentPerformanceDetailTable: React.FC<AgentPerformanceDetailTableProps> = ({
  limit = 20,
  searchTerm,
  onSearchChange,
  dateParams,
}) => {
  const [pageSize, setPageSize] = useState(limit);
  const isFirstRender = useRef(true);

  const {
    pagedData,
    columns,
    isLoading,
    error,
    currentPage,
    processPageChange,
    toggleRowExpansion,
    isModalOpen,
    openModal,
    closeModal,
  } = useAgentMetricsTable({ limit: pageSize, searchTerm, dateParams });

  useEffect(() => {
    if (isFirstRender.current) { isFirstRender.current = false; return; }
    processPageChange(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageSize]);

  const [modalType, setModalType] = React.useState<ModalType>(null);
  const [modalData, setModalData] = React.useState<{
    handledCallIds: string[];
    transferredCallIds: string[];
    callIdsSLA?: string[];
    totalSLACalls?: number;
    agentName?: string;
    queueName?: string;
  }>({
    handledCallIds: [],
    transferredCallIds: [],
    callIdsSLA: [],
    totalSLACalls: 0,
    agentName: undefined,
    queueName: undefined,
  });

  const handleCellClick = (
    columnKey: string,
    handledCallIds: string[],
    transferredCallIds: string[],
    callIdsSLA?: string[],
    totalSLACalls?: number,
    agentName?: string,
    queueName?: string
  ) => {
    setModalData({ handledCallIds, transferredCallIds, callIdsSLA, totalSLACalls, agentName, queueName });
    if (columnKey === 'transferredRate') {
      setModalType('transferredRate');
    } else if (columnKey === 'sla') {
      setModalType('sla');
    } else {
      setModalType('handledCalls');
    }
    openModal();
  };

  const handleCloseModal = () => {
    closeModal();
    setModalType(null);
  };

  return (
    // eslint-disable-next-line no-restricted-syntax -- Card container border/bg, not table row/header coloring.
    <Card className="w-full min-w-0 max-w-full overflow-hidden h-[580px] flex flex-col dark:bg-[#0C3451] dark:text-white dark:border-[#0C3451] rounded-lg relative">
      <AgentMetricsTableHeader
        searchTerm={searchTerm}
        onSearchChange={onSearchChange}
        startDate={dateParams?.startDate}
        endDate={dateParams?.endDate}
        agentIds={dateParams?.agentMicrosoftIds}
      />
      <div className="flex-1 min-h-0 min-w-0 overflow-hidden">
        <AgentMetricsTableContent
          data={pagedData?.data || []}
          columns={columns}
          isLoading={isLoading}
          onToggleExpansion={toggleRowExpansion}
          onCellClick={handleCellClick}
        />
      </div>
      <TablePagingFooter
        currentPage={pagedData?.page ?? currentPage}
        totalPages={pagedData?.totalPages ?? 0}
        total={pagedData?.total ?? 0}
        pageSize={pageSize}
        onPageChange={processPageChange}
        isLoading={isLoading}
        pageSizeOptions={[5, 10, 20, 50]}
        onPageSizeChange={setPageSize}
      />
      <TotalHandledCallsModal
        isOpen={isModalOpen && modalType === 'handledCalls'}
        onClose={handleCloseModal}
        dateParams={{ ...dateParams, agentMicrosoftIds: null }}
        callIds={modalData.handledCallIds}
        agentName={modalData.agentName}
        queueName={modalData.queueName}
      />
      <TransferredRateModal
        isOpen={isModalOpen && modalType === 'transferredRate'}
        onClose={handleCloseModal}
        dateParams={{ ...dateParams, agentMicrosoftIds: null }}
        transferredCallIds={modalData.transferredCallIds}
        handledCallIds={modalData.handledCallIds}
        agentName={modalData.agentName}
        queueName={modalData.queueName}
      />
      <SLAModal
        isOpen={isModalOpen && modalType === 'sla'}
        onClose={handleCloseModal}
        dateParams={{ ...dateParams, agentMicrosoftIds: null }}
        callIds={modalData.callIdsSLA || []}
        totalSLACalls={modalData.totalSLACalls || 0}
        agentName={modalData.agentName}
        queueName={modalData.queueName}
      />
    </Card>
  );
};

export default AgentPerformanceDetailTable;
